/**
 * Launcher: organizaciones y módulos del usuario, su cuenta, y —para quien administre una
 * organización— los usuarios de esa organización (docs/FASE-7.md §7, ADR-007).
 *
 * Tres vistas y ningún enrutador. El comentario original fijaba en tres el umbral para traer
 * `react-router-dom`; al llegar, la razón que lo justificaba solo aparece a medias: ninguna vista
 * necesita ser compartible por URL, pero «Usuarios» sí necesita ser **alcanzable** desde fuera —un
 * módulo manda aquí a quien administra la organización (ADR-007)—. Eso se resuelve con un
 * parámetro de entrada (`?vista=usuarios`), no con un enrutador: no hay historial que mantener ni
 * rutas anidadas. El umbral se mantiene en la primera vista que necesite URL propia de verdad.
 *
 * Las organizaciones se cargan una sola vez, aquí, porque las tres consumidoras las comparten: la
 * pestaña «Usuarios» solo existe si alguna trae `canManageMembers`.
 */
import { useAuth } from "@continnua/auth-web/react";
import { useEffect, useState, type ReactElement } from "react";

import { useOrganizaciones } from "./organizaciones";
import { olvidarVistaGuardada, vistaInicial, type Vista } from "./vista-inicial";
import { Inicio } from "./pages/Inicio";
import { MiCuenta } from "./pages/MiCuenta";
import { Usuarios } from "./pages/Usuarios";

const TITULO: Record<Vista, string> = {
  inicio: "Continnua",
  usuarios: "Usuarios · Continnua",
  cuenta: "Mi cuenta · Continnua",
};

export function App(): ReactElement {
  const { status, claims, signIn, signOut, error } = useAuth();
  const [vista, setVista] = useState<Vista>(vistaInicial);
  const { orgs, error: errorOrgs, recargar } = useOrganizaciones();

  // La intención guardada se consume en cuanto hay sesión: si se dejara, la siguiente visita a la
  // raíz en esta misma pestaña volvería a abrir «Usuarios» sin que nadie lo haya pedido. Cuando la
  // autenticación pasa por el IdP, este efecto no llega a correr —la página se va antes—, que es
  // exactamente lo que hace que la intención sobreviva al viaje.
  useEffect(() => {
    if (status !== "authenticated") return;
    olvidarVistaGuardada();
  }, [status]);

  // El estado `loading` dura lo que tarda el intento silencioso, que acaba llevándose la página por
  // delante con una redirección. Que se vea algo mientras tanto evita el parpadeo en blanco.
  useEffect(() => {
    document.title = TITULO[vista];
  }, [vista]);

  if (status === "loading") {
    return (
      <main className="shell">
        <p className="muted">Comprobando tu sesión…</p>
      </main>
    );
  }

  if (status === "anonymous") {
    return (
      <main className="shell">
        <h1 className="brand">Continnua</h1>
        <p className="muted">Entra una vez y pasa de un módulo a otro sin volver a identificarte.</p>
        {error !== null && <p className="error">{error}</p>}
        <button type="button" className="primary" onClick={() => void signIn()}>
          Entrar
        </button>
      </main>
    );
  }

  // Mientras `orgs` es `null` la pestaña no se pinta: mostrarla y quitarla al llegar la respuesta
  // mueve los botones bajo el cursor. Al terminar de cargar aparece, y no antes.
  const puedeGestionar = (orgs ?? []).some((entrada) => entrada.canManageMembers);

  return (
    <main className="shell">
      <header className="topbar">
        <div>
          <div className="brand">Continnua</div>
          <div className="muted">{claims?.sub ? "Sesión iniciada" : ""}</div>
        </div>
        <nav style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => setVista("inicio")} disabled={vista === "inicio"}>
            Mis módulos
          </button>
          {puedeGestionar && (
            <button type="button" onClick={() => setVista("usuarios")} disabled={vista === "usuarios"}>
              Usuarios
            </button>
          )}
          <button type="button" onClick={() => setVista("cuenta")} disabled={vista === "cuenta"}>
            Mi cuenta
          </button>
          <button type="button" onClick={signOut}>
            Salir
          </button>
        </nav>
      </header>

      {vista === "inicio" && <Inicio orgs={orgs} error={errorOrgs} />}
      {vista === "usuarios" && <Usuarios orgs={orgs} error={errorOrgs} onCambio={recargar} />}
      {vista === "cuenta" && <MiCuenta />}
    </main>
  );
}
